<template>
  <Card
    class="header"
    :footerDivider="false"
    :headerDivider="false"
    :titleFontsizeBig="false"
  >
    <template v-slot:content>
      <div>
        <p class="header-temp">{{ $t('creation.general.title') }}</p>
        <p class="description" v-dompurify-html="$t('creation.general.description')"></p>
        <p
          v-if="showErrorResponse"
          class="error"
          v-dompurify-html="$t('creation.general.error')"
        ></p>
        <BasicTextfield
          @input="updateVsnrValue"
          id="creation.vsnr"
          :value="vsnrValue"
          :label="$t('creation.vsnr.label')"
          :status="vsnrTextfieldStatus"
          :showInfoIcon="true"
          @keyup.enter="onSubmit"
          @update:blur="validateVsnr"
        >
          <template v-slot:error>
            {{ $t('creation.vsnr.error') }}
          </template>
          <template v-slot:info>
            <div v-dompurify-html="$t('creation.vsnr.info')"></div>
          </template>
        </BasicTextfield>
        <BasicTextfield
          @input="updateEmailValue"
          id="creation.email"
          type="email"
          :value="emailValue"
          :label="$t('creation.email.label')"
          :status="emailTextfieldStatus"
          @update:input="triggerEmailRepeat"
          @update:blur="validateEmail"
          @keyup.enter="onSubmit"
        >
          <template v-slot:error>
            <div v-dompurify-html="$t('creation.email.error')"></div>
          </template>
          <template v-slot:info>
            {{ $t('creation.email.info') }}
          </template>
        </BasicTextfield>
        <BasicTextfield
          v-show="showEmailRepeat"
          @input="updateEmailRepeatValue"
          id="creation.email.repeat"
          type="email"
          :value="emailRepeat"
          :label="$t('creation.emailRepeat.label')"
          :status="emailRepeatTextfieldStatus"
          @keyup.enter="onSubmit"
          @update:blur="validateEmailRepeat"
        >
          <template v-slot:error>
            <div v-dompurify-html="$t('creation.emailRepeat.error')"></div>
          </template>
          <template v-slot:info>
            {{ $t('creation.emailRepeat.info') }}
          </template>
        </BasicTextfield>
      </div>
    </template>
    <template v-slot:footer>
      <div class="button-style">
        <CustomButton
          @click="onSubmit"
          buttonId="creation.registrieren-button"
          :buttonColor="ButtonColor.PRIMARY"
          :buttonSize="ButtonSize.BIG"
          :pendingState="pending"
          trackId="register"
          >{{ $t('creation.general.button-registration') }}
        </CustomButton>
      </div>
    </template>
  </Card>
</template>

<script setup lang="ts">
import {
  BasicTextfield,
  ButtonColor,
  ButtonSize,
  Card,
  CustomButton,
  InputStatus
} from 'de-zurich-ui-library'
import { navigate } from '@/utils/NavigationUtils'
import { escapeVSNR } from '@/utilsTenant/rules'
import {
  checkIfEmailInputIsValid,
  checkIfVsnrInputIsValid,
  is5xxError,
  isError400UserExisting
} from '@/components/account-management/accountcreation/accountcreationUtils'
import { ref } from 'vue'
import { createAccountForExistingCustomer } from '@/stores/accountManagementService'
import { ResponseError } from 'account-api'
import type { OrchestrateAccountsResponse } from 'account-api'

const vsnrValue = ref('')
const vsnrTextfieldStatus = ref<InputStatus>(InputStatus.DEFAULT)
const emailValue = ref('')
const emailTextfieldStatus = ref<InputStatus>(InputStatus.DEFAULT)
const pending = ref(false)
const showErrorResponse = ref(false)
const emailRepeat = ref('')
const emailRepeatTextfieldStatus = ref<InputStatus>(InputStatus.DEFAULT)
const showEmailRepeat = ref(false)

const emit = defineEmits<{
  'account:created': [response?: OrchestrateAccountsResponse]
  goToPostcodeAndBirthdate: [vsnr: string, email: string]
}>()

const triggerEmailRepeat = () => {
  showEmailRepeat.value = true
}

function updateVsnrValue(val: string) {
  vsnrValue.value = val.trim()
}

function validateVsnr() {
  vsnrTextfieldStatus.value = checkIfVsnrInputIsValid(vsnrValue.value)
}

function updateEmailValue(val: string) {
  triggerEmailRepeat()
  emailValue.value = val.trim()
}

function updateEmailRepeatValue(val: string) {
  emailRepeat.value = val.trim()
}

const onSubmit = async () => {
  validateInput()

  if (
    emailTextfieldStatus.value !== InputStatus.VALID ||
    vsnrTextfieldStatus.value !== InputStatus.VALID ||
    emailRepeatTextfieldStatus.value !== InputStatus.VALID
  ) {
    return
  }

  await sendCreateAccountRequest()
}

function validateEmail() {
  emailTextfieldStatus.value = checkIfEmailInputIsValid(emailValue.value)
}

function validateEmailRepeat() {
  emailRepeatTextfieldStatus.value =
    emailValue.value === emailRepeat.value ? InputStatus.VALID : InputStatus.ERROR
}

const validateInput = () => {
  validateVsnr()
  validateEmail()
  validateEmailRepeat()
}

const sendCreateAccountRequest = async () => {
  pending.value = true

  try {
    const response = await createAccountForExistingCustomer(
      escapeVSNR(vsnrValue.value),
      emailValue.value
    )
    // Erfolg (kein throw)
    emit('account:created', response)
    pending.value = false
    showErrorResponse.value = false
  } catch (e) {
    pending.value = false
    if (e instanceof ResponseError) {
      const status = e.response.status
      if (is5xxError(status)) {
        navigate(globalThis.ENV_CONFIG?.REDIRECTS.TECHNICAL_ERROR_PAGE)
        return
      }
      // Business-Error: Code aus Response lesen
      const userExists = await isError400UserExisting(e.response)
      if (userExists) {
        navigate(globalThis.ENV_CONFIG?.REDIRECTS.USER_EXISTS_PAGE)
      } else {
        emit('goToPostcodeAndBirthdate', vsnrValue.value, emailValue.value)
        showErrorResponse.value = true
      }
    } else {
      // Nicht-ResponseError (Netzwerk)
      navigate(globalThis.ENV_CONFIG?.REDIRECTS.TECHNICAL_ERROR_PAGE)
    }
  }
}
</script>

<style lang="scss" scoped>
span.error-message {
  font-size: 24px;
  color: var(--input-color-error);
}

b.error-message {
  padding-left: 5px;
  color: var(--input-color-error);
}
</style>
